// Flanking Maneuver group-teleport helpers. Pure. No React, no Three.js.
//
// ── Codex (Flanking Maneuver, Air Magic) ──
//   "Teleport any number of allies at one location to another location
//    a chess knight's move away. Draw a card."
//
// The teleport moves EVERY allied unit at the chosen source cell as a
// group to a single destination a knight's move away. This module owns
// the two read-only set computations the runtime needs:
//   • which source cells hold at least one allied, force-movable unit
//   • which allied, force-movable units sit on a given source cell
//
// Destination geometry is the shared `knightMoveCells` helper. A
// knight's move from the source cell, on-grid. The runtime drives the
// two-stage cell pick (source → knight destination), performs the
// synced moves, and the chained draw.
//
// "Ally" = a unit (Minion or Avatar) controlled by the casting player.
// Controller is read off `card.rotated` ('p2' when rotated, else 'p1'),
// matching every other controller-relative scope in the engine. Sites
// and Auras are the location, not units, so they're excluded; units
// with the `unmovable` status (Talamh Dreig) can't be force-moved, so
// a source cell holding ONLY unmovable units is not a legal source.

import { countsAsMinion } from '../../../core/typeline';
import { isForceMovable } from '../../forcedMovement';

function isUnit(card) {
  return countsAsMinion(card) || card.type === 'Avatar';
}

/**
 * Allied, force-movable units standing on cell (col, row). The casting
 * player's side is `controller` ('p1' | 'p2').
 *
 * @param meshes      host.meshes Map
 * @param cellAt      (x, z) => { col, row } | null
 * @param col / row   the source cell
 * @param controller  'p1' | 'p2'. The casting player
 * @returns cardInstance[]. The units that would teleport together
 */
export function alliedUnitsAtCell(meshes, cellAt, col, row, controller) {
  const out = [];
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !isUnit(card)) continue;
    if (!isForceMovable(card)) continue;
    const side = card.rotated ? 'p2' : 'p1';
    if (side !== controller) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell || cell.col !== col || cell.row !== row) continue;
    out.push(card);
  }
  return out;
}

/**
 * Source cells that hold at least one allied, force-movable unit, as a
 * Set of "col,row" keys. These are the legal first-stage picks.
 *
 * @param meshes      host.meshes Map
 * @param cellAt      (x, z) => { col, row } | null
 * @param controller  'p1' | 'p2'. The casting player
 * @returns Set<string> of "col,row" keys
 */
export function sourceCellsWithAllies(meshes, cellAt, controller) {
  const out = new Set();
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card || !isUnit(card)) continue;
    if (!isForceMovable(card)) continue;
    const side = card.rotated ? 'p2' : 'p1';
    if (side !== controller) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    out.add(`${cell.col},${cell.row}`);
  }
  return out;
}
